// src/hooks/useRecentUploads.js
import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

/**
 * React Query hook for fetching the most recently uploaded Cloudinary
 * assets across all projects. Powers the Admin Uploads Center feed.
 *
 * Wraps the `getRecentUploads` Firebase Callable Function, which
 * queries Cloudinary's Admin API and normalizes the structured
 * upload context (project, category, uploader) into flat records.
 *
 * Defaults to a 5-minute staleTime so rapid navigation between admin
 * pages does not trigger redundant Cloudinary Admin API calls.
 *
 * @param {number} [limit=10] - Max number of uploads to fetch
 * @return {import("@tanstack/react-query").UseQueryResult<Array<Object>>}
 *   React Query result whose `data` is an array of normalized upload
 *   records, each shaped like:
 *   {
 *     public_id, url, secure_url, format, bytes, created_at,
 *     original_filename, project_id, project_name, category,
 *     category_label, source_table, uploaded_by, uploaded_by_name,
 *     extras
 *   }
 *
 * @example
 * const { data: uploads = [], isLoading } = useRecentUploads(25);
 * // Render `uploads.map(u => u.secure_url)` etc.
 */
export const useRecentUploads = (limit = 10) => {
  return useQuery({
    queryKey: ["recentUploads", limit],
    queryFn: async () => {
      const getRecentUploads = httpsCallable(functions, "getRecentUploads");
      const response = await getRecentUploads({ limit });

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to fetch recent uploads",
        );
      }

      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export default useRecentUploads;
